<template>
  <v-text-field
    ref="field"
    :value="text"
    placeholder="0'0&quot;"
    v-bind="$attrs"
    :rules="[validateDistance, ...parsedRules]"
    @input="onInput"
    @blur="onBlur"
    @beforeinput="onBeforeInput"
    @keydown="onKeydown"
    @paste="onPaste"
  />
</template>

<script lang="ts">
import Vue from "vue";
import { formatDistance, parseDistance } from "@/helpers/distance-utils";

const allowedCharacters = /^[0-9.'" ]*$/;

export default Vue.extend({
  name: "DistanceInput",
  inheritAttrs: false,
  props: {
    value: Number,
    required: Boolean,
    rules: Array,
  },
  data() {
    return {
      text: formatDistance(this.value),
    };
  },
  computed: {
    parsedRules(): unknown[] {
      return this.rules || [];
    },
  },
  watch: {
    value(value: number | null) {
      this.text = formatDistance(value);
    },
  },
  methods: {
    validate(): boolean {
      return (this.$refs.field as Vue & { validate: () => boolean }).validate();
    },
    validateDistance(value: string): true | string {
      if (!value || !value.trim()) return this.required ? "Distance is required" : true;
      const totalInches = parseDistance(value);
      if (totalInches === undefined) return "Enter a distance under 100 feet with no more than 12 inches";
      if (this.required && totalInches === 0) return "Distance must be greater than zero";
      return true;
    },
    onInput(value: string) {
      if (allowedCharacters.test(value)) {
        this.text = value;
      }
    },
    onBlur(event: FocusEvent) {
      const totalInches = parseDistance(this.text);
      if (totalInches === null) {
        this.text = "";
        this.$emit("input", null);
      } else if (totalInches !== undefined && (!this.required || totalInches > 0)) {
        this.text = formatDistance(totalInches);
        this.$emit("input", totalInches);
      }
      this.$emit("blur", event);
    },
    onBeforeInput(event: InputEvent) {
      if (event.data && !allowedCharacters.test(event.data)) event.preventDefault();
    },
    onKeydown(event: KeyboardEvent) {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.length === 1 && !allowedCharacters.test(event.key)) event.preventDefault();
    },
    onPaste(event: ClipboardEvent) {
      const value = event.clipboardData?.getData("text") || "";
      if (!allowedCharacters.test(value)) event.preventDefault();
    },
  },
});
</script>
